import "dexie-export-import";

import Dexie, { type IndexableType, type Table } from "dexie";
import { v4 as uuidv4 } from "uuid";

export interface DebugEvent {
    timestamp: number;
    type: string;
    data: unknown;
}
interface Settings {
    id: "general";
    debug?: boolean;
    sim_score_threshold?: number;
    sim_k_limit?: number;
    stream?: boolean;
    suggestions?: boolean;
    use_agents?: boolean;
    chat_vector_index?: string;
}

export enum ChatRole {
    SYSTEM = "system",
    USER = "user",
    ASSISTANT = "assistant",
}

export const CTS_USER_CHAT = "cts_user_chat";
export const TRIP_ANALYZER = "tripanalyzer";
export const RDC_NOTIFICATION_MAPPINGS = "rdc_notification_mappings";

export enum ColumnType {
    STRING = "string",
    NUMBER = "number",
    BOOLEAN = "boolean",
    DATE = "date",
    DATE_RANGE = "date_range",
    DICT = "dict",
    ARRAY = "array",
    URL = "url",
}

export type IndexDescription = {
    description: string;
    header_name: string;
    name: string;
    type: ColumnType;
    end_field?: string;
    facet?: boolean;
};

export type DataSourceWithIndexDescription = {
    namespace: string;
    index_description: IndexDescription[];
};

export type IndexInfo = {
    index_prefix: string;
    index_id: string;
    label: string;
    product_lines?: string[];
    version?: string;
    default_date_field?: string;
    index_description: IndexDescription[];
};

export type AccessPolicy = "role" | "always_on" | "synced" | "admin_assigned";

/** Policies that cannot be requested; shown locked like synced in prefs / KB details. */
export const isNonRequestableAccessPolicy = (policy?: AccessPolicy) =>
    policy === "synced" || policy === "admin_assigned";

export const isAdminAssignableAccessPolicy = (policy?: AccessPolicy) =>
    policy === "role" || policy === "admin_assigned";

export type RawDatasource = {
    label: string;
    id: string;
    description: string;
    // access_policy/available are user-specific and only populated once merged with
    // /v1/user data (see useDatasourceOverviewWithAccess) - absent on the raw
    // /v1/system/configuration response.
    access_policy?: AccessPolicy;
    available?: boolean;
    // True when this datasource feeds the assets viewer rather than the documents
    // viewer. Sourced from /v1/system/configuration; absent on older backends.
    asset_datasource?: boolean;
    ingestion_status: string;
    latest_document_timestamp: string;
    total_count: string;
    product_lines: string[];
    index: IndexInfo[];
};

export type ChatVectorIndexOption = {
    label: string;
    id: string;
    datasource_id: string;
    datasource_label: string;
    index_id: string;
    index_description: IndexDescription[];
    asset_datasource?: boolean;
};

export class ChatEntity {
    id!: string;
    description!: string;
    totalTokens!: number;
    createdAt!: number;
    updatedAt?: Date;
    totalMessages?: number;
    vectorIndex!: string;
    suggestedMessages!: string[];

    static _(vectorIndex: string) {
        const record = new ChatEntity();
        record.id = uuidv4();
        record.description = `Chat-${record.id.slice(0, 8)}`;
        record.totalTokens = 0;
        record.createdAt = Date.now();
        record.vectorIndex = vectorIndex;
        record.suggestedMessages = [];

        return record;
    }

    setId(id: string) {
        this.id = id;
        return this;
    }

    setDescription(description: string) {
        this.description = (description || "New Chat").trim();
        return this;
    }

    setTotalTokens(totalTokens: number) {
        this.totalTokens = totalTokens;
        return this;
    }

    add() {
        return db.chats.add(this);
    }
}

interface SuggestionOption {
    text: string;
    score: number;
    freq: number;
}

export type SingleSuggestionOption = SuggestionOption & { oldWord: string };

interface Suggestion {
    text: string;
    oldWord?: string;
    offset: number;
    length: number;
    options: SuggestionOption[];
}

export interface KeySuggestion {
    key: string;
    suggestions: Suggestion[];
}
export type TimeseriesMetric = {
    label?: string;
    value: number;
    unit?: string | null;
    timestamp?: string | null;
};

export interface Reference {
    reference_id: string;
    title: string;
    source: string;
    dataSourceType: string;
    page_numbers: Set<number>;
    plotlyFigure?: {
        data: unknown[];
        layout?: Record<string, unknown>;
        [key: string]: unknown;
    };
    displayMode?: "chart" | "metric";
    metric?: TimeseriesMetric;
}

// This is the format of the reference that we get from the API.
export interface StreamedReference
    extends Omit<
        Reference,
        "dataSourceType" | "page_numbers" | "plotlyFigure" | "displayMode" | "metric"
    > {
    data_source_type: string;
    page_number: number | null;
    plotly_figure?: Reference["plotlyFigure"];
    display_mode?: "chart" | "metric";
    metric?: TimeseriesMetric;
}

export class MessageEntity {
    id!: IndexableType;
    toolCall = false;
    toolCallCount = 0;
    toolActivities: { internalName: string; done: boolean; error?: boolean }[] = [];
    chatId!: IndexableType;
    responseId!: string;
    content!: string;
    pretrained!: string;
    done?: boolean;
    debug?: DebugEvent[];
    hasError?: boolean;
    error?: string;
    suggestions?: SingleSuggestionOption[];
    role!: ChatRole;
    createdAt!: number;
    repliedId!: IndexableType;
    references: Reference[] = [];
    datasources?: string[];

    static _() {
        const record = new MessageEntity();
        record.id = uuidv4();
        record.createdAt = Date.now();
        return record;
    }

    setId(id: IndexableType) {
        this.id = id;
        return this;
    }

    setResponseId(responseId: string) {
        this.responseId = responseId;
        return this;
    }

    setChatId(id: IndexableType) {
        this.chatId = id;
        return this;
    }

    setContent(content?: string) {
        this.content = (content ?? "unknown response").trim();
        return this;
    }

    setRole(role: ChatRole) {
        this.role = role;
        return this;
    }

    setRepliedId(repliedId: IndexableType) {
        this.repliedId = repliedId;
        return this;
    }

    setDatasources(datasources: string[]) {
        this.datasources = datasources;
        return this;
    }

    setIsLoading() {
        this.done = false;
        return this;
    }

    toJSON(): Omit<
        MessageEntity,
        | "setId"
        | "setResponseId"
        | "setChatId"
        | "setContent"
        | "setRole"
        | "setRepliedId"
        | "setDatasources"
        | "setIsLoading"
        | "toJSON"
        | "add"
    > {
        return {
            id: this.id,
            toolCall: this.toolCall,
            toolCallCount: this.toolCallCount,
            toolActivities: this.toolActivities,
            chatId: this.chatId,
            responseId: this.responseId,
            content: this.content,
            pretrained: this.pretrained,
            done: this.done,
            debug: this.debug,
            hasError: this.hasError,
            error: this.error,
            suggestions: this.suggestions,
            role: this.role,
            createdAt: this.createdAt,
            repliedId: this.repliedId,
            references: this.references,
            datasources: this.datasources,
        };
    }

    add() {
        return db.messages.add(this.toJSON() as MessageEntity);
    }
}

class Database extends Dexie {
    chats!: Table<ChatEntity>;
    messages!: Table<MessageEntity>;
    settings!: Table<Settings>;

    constructor() {
        super("CTS-KS-7.2");
        this.version(8).stores({
            chats: "id, createdAt",
            messages: "id, chatId, responseId, createdAt",
            settings: "id",
        });

        this.on("populate", async () => {
            db.settings.add({
                id: "general",
            });
        });

        // Open eagerly so a corrupted backing store (Edge/Chromium IndexedDB bug) is caught
        // here rather than surfacing as an unhandled rejection on the first query.
        this.open().catch(async (err: { name?: string }) => {
            if (err?.name === "UnknownError" || err?.name === "DatabaseClosedError") {
                try {
                    await Dexie.delete("CTS-KS-7.2");
                    await this.open();
                } catch {
                    // Unrecoverable — app continues without local persistence
                }
            }
        });
    }
}
export const db = new Database();

export const updateSettings = <T extends keyof Omit<Settings, "id">>(
    key: T,
    value: Settings[T],
) => {
    db.settings.where({ id: "general" }).modify((settings) => {
        // biome-ignore lint/suspicious/noExplicitAny: Dynamic key assignment requires any
        (settings as any)[key] = value;
    });
};
